'use client';
import { useTranslation } from '@/hook';
import Typography from '@/components/shared/typography/typography.component';
import { CarTypesEnum } from '@/components/car-details-v2/car-type';
import {
    CarIncludesMap,
    AvailableCarIncludes,
} from '../CarIncludes/car-includes.type';
import { AVAILABLE_LANGUAGES } from '@/constants';
import Image from 'next/image';
import { useState } from 'react';
import { useAppSelector } from '@/hook';
import { CarIncludesContent } from '../CarIncludes/CarIncludesContent';
import {
    SylndrCertifiedShield,
    SylndrLiteStars,
    dealerIcon,
    SylndrCertifiedShieldDesktop,
    SylndrLiteStarsDesktop,
    dealerIconDesktop,
    whiteInfo,
    InfoGrayIconSVG,
    checkCircleSylndr,
    checkCircleDealer,
    checkCircleLite,
} from 'public/Images';

const CertifiedIcon = () => (
    <Image src={SylndrCertifiedShield} alt="Certified" width={16} height={16} />
);
const LiteIcon = () => (
    <Image src={SylndrLiteStars} alt="Lite" width={16} height={16} />
);
const DealerIcon = () => (
    <Image src={dealerIcon} alt="Dealer" width={16} height={16} />
);

const CertifiedIconDesktop = () => (
    <Image src={SylndrCertifiedShieldDesktop} alt="Certified" />
);
const LiteIconDesktop = () => <Image src={SylndrLiteStarsDesktop} alt="Lite" />;
const DealerIconDesktop = () => <Image src={dealerIconDesktop} alt="Dealer" />;
const CertifiedCheckIcon = () => (
    <Image src={checkCircleSylndr} alt="Check" width={12} height={12} />
);
const LiteCheckIcon = () => (
    <Image src={checkCircleLite} alt="Check" width={12} height={12} />
);
const DealerCheckIcon = () => (
    <Image src={checkCircleDealer} alt="Check" width={12} height={12} />
);
const InfoIcon = () => (
    <Image src={whiteInfo} alt="Info" width={16} height={16} />
);

const InfoGrayIconComponent = () => (
    <Image src={InfoGrayIconSVG} alt="Info" width={13} height={13} />
);

const types = {
    [CarTypesEnum.CERTIFIED]: {
        bgColor: 'bg-certified-600',
        textColor: 'text-certified-700',
        icon: <CertifiedIcon />,
        desktopIcon: <CertifiedIconDesktop />,
        checkIcon: <CertifiedCheckIcon />,
        label: 'Certified',
    },
    [CarTypesEnum.LITE]: {
        bgColor: 'bg-lite-600',
        textColor: 'text-lite-700',
        icon: <LiteIcon />,
        desktopIcon: <LiteIconDesktop />,
        checkIcon: <LiteCheckIcon />,
        label: 'Lite',
    },
    [CarTypesEnum.Dealer]: {
        bgColor: 'bg-dealer-600',
        textColor: 'text-dealer-700',
        icon: <DealerIcon />,
        desktopIcon: <DealerIconDesktop />,
        checkIcon: <DealerCheckIcon />,
        label: 'carTypes.certifiedDealer',
    },
};

interface Props {
    type: CarTypesEnum;
    isTop: boolean;
    lang: AVAILABLE_LANGUAGES;
}

interface DesktopProps {
    type: CarTypesEnum;
    lang?: AVAILABLE_LANGUAGES;
}

export const CarTypeBannerMobile = ({ type, isTop, lang }: Props) => {
    const { t } = useTranslation();
    const [openContent, setOpenContent] = useState(false);
    const { selectedCar } = useAppSelector(state => state.checkout);

    // Get benefits data from CarIncludesMap
    const benefitIndices = CarIncludesMap[type];
    const carBenefitsData = benefitIndices.map(index => ({
        title: AvailableCarIncludes[index].title[lang],
    }));

    const bannerText = t('carTypes.bannerText');
    const labelText = t(types[type].label);
    const textColorClass = types[type].textColor;
    const bgColorClass = types[type].bgColor;

    return (
        <>
            <div
                className={`flex flex-row gap-1.5 p-[8px_16px] justify-center items-center w-full  relative ${bgColorClass} cursor-pointer`}
                onClick={() => setOpenContent(true)}
            >
                <Typography variant="captionMedium" color="white">
                    {bannerText}
                </Typography>
                <div className="flex flex-row items-center bg-white rounded-full gap-1.5 p-[5px_10px_3px_8px]">
                    {types[type].icon}
                    <Typography
                        variant="captionBold"
                        className={textColorClass}
                    >
                        {labelText}
                    </Typography>
                </div>
                <InfoIcon />
            </div>
            {!isTop && (
                <div
                    className="flex flex-row gap-2 bg-white p-[6px_12px] w-full cursor-pointer"
                    onClick={() => setOpenContent(true)}
                >
                    <div className="flex gap-2 flex-row overflow-x-auto flex-nowrap scrollbar-none flex-1">
                        {carBenefitsData.map((benefit, index) => (
                            <div
                                className="flex gap-1 flex-row items-center flex-shrink-0 whitespace-nowrap"
                                key={index}
                            >
                                {types[type].checkIcon}
                                <Typography
                                    variant="captionMedium"
                                    className={textColorClass}
                                >
                                    {benefit.title}
                                </Typography>
                            </div>
                        ))}
                    </div>
                    <div className="flex-shrink-0">
                        <InfoGrayIconComponent />
                    </div>
                </div>
            )}
            {openContent && (
                <CarIncludesContent
                    open={openContent}
                    setOpen={setOpenContent}
                    IS_DESKTOP={false}
                    showCarDetailsHeader={false}
                    selectedCar={selectedCar}
                    carType={type}
                />
            )}
        </>
    );
};

export const CarTypeBannerDesktop = ({
    type,
    lang = AVAILABLE_LANGUAGES.ar,
}: DesktopProps) => {
    const { t } = useTranslation();
    const [openContent, setOpenContent] = useState(false);
    const { selectedCar } = useAppSelector(state => state.checkout);
    const bgColorClass = types[type].bgColor;
    const labelText = t(types[type].label);

    return (
        <>
            <div
                className={`hidden md:flex flex-row items-center h-[30px] px-3 py-1.5 min-w-[110px] ${bgColorClass} cursor-pointer`}
                style={{
                    borderRadius:
                        lang === AVAILABLE_LANGUAGES.en ? '12px 0' : '0 12px',
                }}
                onClick={() => setOpenContent(true)}
            >
                <div className="me-1.5 flex-shrink-0">
                    {types[type].desktopIcon}
                </div>
                <Typography
                    className="me-3 mt-1"
                    variant="captionSemiBold"
                    color="white"
                >
                    {labelText}
                </Typography>
                <InfoIcon />
            </div>
            {openContent && (
                <CarIncludesContent
                    open={openContent}
                    setOpen={setOpenContent}
                    IS_DESKTOP={true}
                    showCarDetailsHeader={false}
                    selectedCar={selectedCar}
                    carType={type}
                />
            )}
        </>
    );
};
